import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Grid, Typography,
} from '@mui/material';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import InvButton from '../../../components/InvButton';
import { formatDateTime } from '../../../utils';
import { IPD_COLORS, cardSx } from '../../../theme';
import { SummaryStat, DetailSummaryGrid } from '../PharmacyUi';
import PrescribedMedicinesTable from './PrescribedMedicinesTable';

function NotesBlock({ label, children }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary, mt: 0.25 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * OPD prescription detail. Unlike the IPD dialog this does not re-fetch — the
 * search response already carries the whole prescription and there is no
 * per-prescription detail endpoint.
 */
export default function OpdPrescriptionViewDialog({ open, prescription, onClose, onStartDispense }) {
  if (!prescription) return null;

  const patient = prescription.patient || {};
  const medications = prescription.medications || [];
  const complaints = prescription.chief_complaints || [];
  const ageGender = [patient.age, patient.gender].filter((v) => v != null && v !== '').join(' · ');
  const medicineCount = medications.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="opd-prescription-dialog-title"
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
    >
      <DialogTitle id="opd-prescription-dialog-title" sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        color: IPD_COLORS.textPrimary, pb: 1,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '10px',
          backgroundColor: IPD_COLORS.selectedBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <LocalPharmacyIcon sx={{ color: IPD_COLORS.primary }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{patient.name || '—'}</Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            {formatDateTime(prescription.datetime)}
            {prescription.doctor_name ? ` · ${prescription.doctor_name}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: IPD_COLORS.border }}>
        <DetailSummaryGrid>
          <Grid item xs={6} sm={3}><SummaryStat label="Patient" value={patient.name || '—'} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Age / Gender" value={ageGender || '—'} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Phone" value={patient.phone_no || '—'} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="ABHA" value={patient.abha_id || patient.abha_no || '—'} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Doctor" value={prescription.doctor_name || '—'} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Facility" value={prescription.facility_name || '—'} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Prescribed On" value={formatDateTime(prescription.datetime)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Medicines" value={medicineCount} /></Grid>
        </DetailSummaryGrid>

        {prescription.diagnosis && <NotesBlock label="Diagnosis">{prescription.diagnosis}</NotesBlock>}
        {complaints.length > 0 && (
          <NotesBlock label="Chief Complaints">{complaints.join(', ')}</NotesBlock>
        )}
        {prescription.clinical_notes && <NotesBlock label="Clinical Notes">{prescription.clinical_notes}</NotesBlock>}

        <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary, mb: 1.5, mt: 2 }}>
          Prescribed Medicines ({medicineCount})
        </Typography>
        <PrescribedMedicinesTable medications={medications} />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
          Close
        </Button>
        {medicineCount > 0 && (
          <InvButton onClick={() => { onClose(); onStartDispense?.(prescription); }}>
            Start Dispense ({medicineCount} medicine{medicineCount > 1 ? 's' : ''})
          </InvButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
